import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { resetCompanyInfo, setCompanyBranding, CompanyState } from '../../Store/companySlice';
import { API_URL } from '../../config';

/**
 * `useCompany()` — canonical accessor for the currently-hydrated company
 * branding state, plus a `refresh()` action that re-fetches the public
 * branding endpoint and re-applies title / favicon / brand CSS variables.
 *
 * Call `refresh()` after saving Company Settings so the UI updates the
 * name, logo, favicon and colors immediately — no logout, no page reload.
 */
export function useCompany() {
  const dispatch = useDispatch();
  const company = useSelector((s: any) => s?.company as CompanyState | undefined) ?? undefined;

  const refresh = useCallback(async (): Promise<void> => {
    const base = String(API_URL || '').replace(/\/+$/, '');
    try {
      const res = await fetch(`${base}/company/branding`, { credentials: 'omit', cache: 'no-store' });
      if (!res.ok) return;
      const body = await res.json();
      const d = body?.data ?? {};
      const branding = d.branding ?? {};
      const payload: Partial<CompanyState> = {
        companyName: d.name ?? '',
        shortName: d.short_name ?? '',
        websiteUrl: d.website_url ?? '',
        branding: {
          logo: branding.logo ?? '',
          loginLogo: branding.login_logo ?? '',
          loginBackground: branding.login_background ?? '',
          favicon: branding.favicon ?? '',
          primaryColor: branding.primary_color ?? '',
          secondaryColor: branding.secondary_color ?? '',
          accentColor: branding.accent_color ?? '',
        },
      };
      // Reset first so consumers observing `hydrated` see a fresh cycle.
      dispatch(resetCompanyInfo());
      dispatch(setCompanyBranding(payload));

      const title = (payload.shortName || payload.companyName || '').trim();
      if (title !== '') document.title = title;

      // Parent Portal iOS home-screen title. The static <meta> ships blank
      // so tenant names never leak into the compiled HTML — we hydrate it
      // here once the tenant's short name is known.
      const appleTitle = document.getElementById('app-apple-web-app-title') as HTMLMetaElement | null;
      if (appleTitle && title !== '') {
        appleTitle.setAttribute('content', `${title} Parent Portal`);
      }

      // Expose company contact email on window for utility components
      // (e.g. AddressAutocomplete's Nominatim usage-policy header) that
      // don't have a natural redux hookup. Kept intentionally minimal.
      const publicName = title || '';
      (window as any).__COMPANY_NAME__ = publicName;

      // Browsers (Chrome especially) aggressively cache favicon by URL,
      // so an admin who re-uploads the file can end up seeing the stale
      // icon in the tab even after the branding endpoint returns the new
      // path. `refresh()` runs on first boot and after Settings save (not
      // per render), so a `?v=<timestamp>` param appended here yields at
      // most two favicon fetches per session — not an endless-URL churn.
      const fav = payload.branding?.favicon;
      if (fav) {
        const bust = fav.startsWith('data:')
          ? fav
          : `${fav}${fav.includes('?') ? '&' : '?'}v=${Date.now()}`;
        const link = document.getElementById('app-favicon') as HTMLLinkElement | null;
        if (link) link.href = bust;
        const apple = document.getElementById('app-apple-touch-icon') as HTMLLinkElement | null;
        if (apple) apple.href = bust;
      }

      const root = document.documentElement;
      const bp = payload.branding?.primaryColor;
      const bs = payload.branding?.secondaryColor;
      const ba = payload.branding?.accentColor;
      if (bp) root.style.setProperty('--brand-primary', bp);
      if (bs) root.style.setProperty('--brand-secondary', bs);
      if (ba) root.style.setProperty('--brand-accent', ba);
    } catch {
      /* best-effort */
    }
  }, [dispatch]);

  return { company, refresh };
}

export default useCompany;
